import React from 'react'
import PropTypes from 'prop-types'

import TextField from '@material-ui/core/TextField'
import Box from '@material-ui/core/Box'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import InputLabel from '@material-ui/core/InputLabel'
import Button from '@material-ui/core/Button'
import InputAdornment from '@material-ui/core/InputAdornment'
import IconButton from '@material-ui/core/IconButton'
import ButtonGroup from '@material-ui/core/ButtonGroup'
import FormControl from '@material-ui/core/FormControl'
import Tooltip from '@material-ui/core/Tooltip'

import { makeStyles } from '@material-ui/core/styles'

import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'
import DeleteIcon from '@material-ui/icons/Delete'
import ReplayIcon from '@material-ui/icons/Replay'
import LaunchIcon from '@material-ui/icons/Launch'

import HotkeyEditorTextField from './HotkeyEditorTextField'

const useStyles = makeStyles({
  root: {
    '& > div': {
      padding: '10px'
    },
    '& input[readonly]': {
      backgroundColor: '#eeeeee',
      cursor: 'not-allowed'
    }
  },
  colorSwatch: {
    height: '24px',
    width: '30px',
    border: 'solid 1px black'
  }
})

// this could potentially be reworked to use react-hook-form but I don't think it is crucial - PJF
export default function HotkeyElementEditor (props) {
  const {
    data,
    onDataChanged,
    onDeleteElementClick,
    onPriorityUpClick,
    onPriorityDownClick,
    onProductSelectClick,
    colors,
    icons,
    fontSizes,
    widths,
    onFormValidationStatusChange
  } = props

  const classes = useStyles()

  const [type, setType] = React.useState(data.type)
  const [typeHuman, setTypeHuman] = React.useState('')
  const [text, setText] = React.useState(data.text || '')
  const [colorId, setColorId] = React.useState(data.colorId || '')
  const [itemSize, setItemSize] = React.useState(data.itemSize || '')
  const [width, setWidth] = React.useState(data.width || '')
  const [fontSize, setFontSize] = React.useState(data.fontSize || '')
  const [iconId, setIconId] = React.useState(data.iconId || '')
  const [priority, setPriority] = React.useState(data.priority)
  const [productId, setProductId] = React.useState(data.productId)

  const [textValidated, setTextValidated] = React.useState(true)
  const [itemSizeValidated, setItemSizeValidated] = React.useState(true)

  const [initialElementLoad, setInitialElementLoad] = React.useState(true)

  // we only reload the data in the form if the id has changed (which would normally trigger the onDataChanged event, so we set the initial load flag)
  React.useEffect(() => {
    console.log('data id has changed', data)
    setInitialElementLoad(true)

    setType(data.type)
    setText(data.text || '')
    setColorId(data.colorId || '')
    setItemSize(data.itemSize || '')
    setWidth(data.width || '')
    setFontSize(data.fontSize || '')
    setIconId(data.iconId || '')
    setPriority(data.priority)
    setProductId(data.productId)

    setTextValidated(true) // we expect it to be in a valid state when we load data into the form
    setItemSizeValidated(true)
  }, [data.id])

  // this will get triggered if we select a product via the SelectProductDialog
  React.useEffect(() => {
    setProductId(data.productId)
    setText(data.text || '')
    setItemSize(data.itemSize || '')
  }, [data.productId])

  React.useEffect(() => {
    let typeHuman = ''

    switch (type) {
      case 'product':
        typeHuman = 'Product Hotkey'
        break
      case 'linebreak':
        typeHuman = 'Line Break'
        break
      case 'spacer':
        typeHuman = 'Spacer'
        break
      case 'menu':
        typeHuman = 'Menu'
        break
      case 'submenu':
        typeHuman = 'Submenu'
        break
    }

    setTypeHuman(typeHuman)
  }, [type])

  React.useEffect(() => {
    if (initialElementLoad) {
      setInitialElementLoad(false)

      return // don't fire the data changed event on initial load of the element
    }

    onDataChanged({
      id: data.id,
      type,
      text,
      fontSize,
      iconId,
      colorId,
      itemSize,
      width,
      priority,
      productId
    })
  }, [type, text, colorId, itemSize, width, fontSize, iconId, productId])

  const handleRevertText = () => {
    setText(data.originalText)
  }

  const handleRevertItemSizeDescription = () => {
    setItemSize(data.originalItemSize)
  }

  // if any element is invalid, then the form is invalid. Currently we only check text, but other bits could be added in the future
  React.useEffect(() => {
    // test for all fields we need to validate here
    if (textValidated && itemSizeValidated) {
      onFormValidationStatusChange(true)
    } else {
      onFormValidationStatusChange(false)
    }
  }, [textValidated, itemSizeValidated]) // all validation states must be in this array

  const fontSizeElements = fontSizes.map((fontSize) => <MenuItem key={fontSize.value} value={fontSize.name}>{fontSize.displayName}</MenuItem>)
  const widthElements = widths.map((width) => <MenuItem key={width.value} value={width.name}>{width.displayName}</MenuItem>)
  const colorElements = colors.map((color) => {
    return (
      <MenuItem key={color.colorId} value={color.colorId}>
        <Box display="flex" alignItems="center">
          <Box className={classes.colorSwatch} style={{ backgroundColor: `${color.value}` }}></Box>
          <Box>
            &nbsp;
            &nbsp;
            {color.name}
          </Box>
        </Box>
      </MenuItem>
    )
  })

  const iconElements = icons.map((icon) => {
    return (
      <MenuItem key={icon.iconId} value={icon.iconId}>
        <Box display="flex" alignItems="center">
          <Box>
            <img src={require(`../../assets/img/icons/hotkeys/light/${icon.name}.svg`)} />
          </Box>
          <Box>
            &nbsp;
            &nbsp;
            {icon.displayName}
          </Box>
        </Box>
      </MenuItem>
    )
  })

  return (
    <>
      <Box className={classes.root} display="flex" flexDirection="column" flex={0.8}>
        <Box display="flex" justifyContent="space-between">
          <ButtonGroup color="primary">
            <Button onClick={() => onPriorityUpClick(data.id)}><KeyboardArrowUpIcon /></Button>
            <Button color="default" style={{ pointerEvents: 'none' }}>Priority</Button>
            <Button onClick={() => onPriorityDownClick(data.id)}><KeyboardArrowDownIcon /></Button>
          </ButtonGroup>
          <ButtonGroup color="secondary">
            <Button onClick={(event) => onDeleteElementClick(event, data.id, type)}><DeleteIcon />&nbsp;Delete</Button>
          </ButtonGroup>
        </Box>

        <Box>
          <TextField
            variant="outlined"
            label="Element Type"
            value={typeHuman}
            InputProps={{
              readOnly: true
            }}
            fullWidth
          />
        </Box>
        {
          data.type === 'product' &&
          <>
            <Box>
              <TextField
                variant="outlined"
                label="Selected Product"
                value={
                  productId !== null
                    ? `${data.originalText} (${data.barcode === null ? 'Unbarcoded' : data.barcode})`
                    : 'No Product Set'
                }
                InputProps={{
                  readOnly: true
                }}
                fullWidth
              />
            </Box>
            <Box marginTop={-2}>
              <Button variant="outlined" color="primary" onClick={onProductSelectClick} fullWidth><LaunchIcon />&nbsp;Select Product</Button>
            </Box>
          </>
        }
        {
          (data.type !== 'spacer' && data.type !== 'linebreak') &&
          <>
            <Box>
              <HotkeyEditorTextField
                label="Text"
                variant="outlined"
                onChange={(value) => setText(value)}
                onValidationStatusChange={(status) => setTextValidated(status)}
                validation="^.{2,100}$"
                errorText="Hotkey text must be between 2 and 100 characters"
                fullWidth
                value={text}
                InputProps={{
                  endAdornment: (data.originalText !== null && data.originalText !== text) &&
                    <InputAdornment position="end">
                      <Tooltip title={ `Revert to original product description - ${data.originalText}` }>
                        <IconButton
                          onClick={handleRevertText}
                        >
                          <ReplayIcon />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                }}
              />
            </Box>
            <Box>
              <FormControl variant="outlined" fullWidth>
                <InputLabel>Font Size</InputLabel>
                <Select label="Font Size" value={fontSize} onChange={(event) => setFontSize(event.target.value)}>
                  {fontSizeElements}
                </Select>
              </FormControl>
            </Box>
          </>
        }
        {
          (data.type === 'menu' || data.type === 'submenu' || data.type === 'product') &&
          <Box>
            <FormControl variant="outlined" fullWidth>
              <InputLabel>Width</InputLabel>
              <Select label="Width" value={width} onChange={(event) => setWidth(event.target.value)}>
                {widthElements}
              </Select>
            </FormControl>
          </Box>
        }
        {
          data.type === 'product' &&
          <Box>
            <FormControl variant="outlined" fullWidth>
              <InputLabel>Colour</InputLabel>
              <Select label="Colour" value={colorId} onChange={(event) => setColorId(event.target.value)}>
                {colorElements}
              </Select>
            </FormControl>
          </Box>
        }
        {
          data.type === 'product' &&
          <Box>
            <HotkeyEditorTextField
              label="Item Size"
              variant="outlined"
              onChange={(value) => setItemSize(value)}
              onValidationStatusChange={(status) => setItemSizeValidated(status)}
              validation="^.{0,20}$"
              errorText="Item Size must be less than 20 characters"
              fullWidth
              value={itemSize}
              InputProps={{
                endAdornment: (data.originalItemSize !== null && data.originalItemSize !== itemSize) &&
                  <InputAdornment position="end">
                    <Tooltip title={ `Revert to original item size description - ${data.originalItemSize}` }>
                      <IconButton
                        onClick={handleRevertItemSizeDescription}
                      >
                        <ReplayIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
              }}
            />
          </Box>
        }
        {
          data.type === 'menu' &&
          <Box>
            <FormControl variant="outlined" fullWidth>
              <InputLabel>Icon</InputLabel>
              <Select label="Icon" value={iconId} onChange={(event) => setIconId(event.target.value)}>
                {iconElements}
              </Select>
            </FormControl>
          </Box>
        }
      </Box>
    </>
  )
}

HotkeyElementEditor.propTypes = {
  data: PropTypes.object.isRequired,
  onDataChanged: PropTypes.func.isRequired,
  onDeleteElementClick: PropTypes.func.isRequired,
  onPriorityUpClick: PropTypes.func.isRequired,
  onPriorityDownClick: PropTypes.func.isRequired,
  onProductSelectClick: PropTypes.func.isRequired,
  colors: PropTypes.array.isRequired,
  icons: PropTypes.array.isRequired,
  fontSizes: PropTypes.array.isRequired,
  widths: PropTypes.array.isRequired,
  onFormValidationStatusChange: PropTypes.func.isRequired
}
